import React from 'react'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'
import OracleIconButton from '../OracleCommons/OracleIconButton'

interface OracleGuideToastNudgeProps {
  /** Whether the toast is visible */
  isVisible: boolean
  /** Called when user clicks the action button */
  onAction: () => void
  /** Called when user dismisses the toast (close button or auto-timeout) */
  onDismiss: (method: 'manual' | 'auto') => void
}

/**
 * Toast nudge that appears as a compact purple pill in the top-right
 * of the video player. Nudges users to click "Break that last part down"
 * to open the AI Guide with a contextual breakdown prompt.
 *
 * Visibility is controlled entirely by the parent via `isVisible`.
 * useConceptTrigger shows the toast at a moment's startSeconds and
 * hides it when playback passes endSeconds, so the toast stays up
 * for the full duration of each dense moment.
 */
export const OracleGuideToastNudge: React.FC<OracleGuideToastNudgeProps> = ({
  isVisible,
  onAction,
  onDismiss,
}) => {
  const actionButtonText = useTranslatedText({ keys: ['toastNudge', 'actionButton'] })
  const ariaCloseText = useTranslatedText({ keys: ['toastNudge', 'ariaClose'] })
  const ariaLabelText = useTranslatedText({ keys: ['toastNudge', 'ariaLabel'] })

  if (!isVisible) {
    return null
  }

  return (
    <div
      className="oracle-guide-toast-nudge"
      role="alert"
      aria-label={ariaLabelText}
    >
      <OracleIconButton
        icon="close"
        size="14px"
        className="oracle-guide-toast-nudge__close-btn"
        onClick={() => onDismiss('manual')}
        aria-label={ariaCloseText}
      />
      <button
        type="button"
        className="oracle-guide-toast-nudge__action-btn"
        onClick={onAction}
      >
        {actionButtonText}
      </button>
    </div>
  )
}

export default OracleGuideToastNudge
